import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useLocale } from '@hooks/useLocale';
import { HookError } from '@adapters/bigcommerce/errorHandlers';
import {
  AvailabilityStatus,
  MecVariant,
  QuickAddProduct,
} from '@adapters/bigcommerce/types/product';
import { quickAddProductFetcher } from './useProduct';
import {
  ColourOption,
  SizeOption,
  getColourOptions,
  getSelectedAvailability,
  getSizeOptionsForColour,
  resolveInitialColourId,
  resolveVariant,
} from './variantAvailability';

/** The SWR key `useSWR` below subscribes to — shared with `prefetch()` here
    and with `usePrefetchQuickAddProducts` so every cache writer/reader
    agrees on the same shape. */
export const quickAddProductCacheKey = (
  locale: string,
  code: string
): [string, string, string] => ['quick-add-product', locale, code];

/** Warms the SWR cache for a single product code without subscribing to it —
    the shared primitive behind this hook's own `prefetch()` (hover-intent,
    one product) and `usePrefetchQuickAddProducts` (an eagerly-preloaded,
    bounded set, e.g. ImageHotspot's hotspots). `revalidate: false` since this
    is a first-write, not a refresh of already-subscribed data. */
export const primeQuickAddProductCache = (
  mutate: ReturnType<typeof useSWRConfig>['mutate'],
  locale: string,
  code: string
) =>
  mutate(
    quickAddProductCacheKey(locale, code),
    quickAddProductFetcher(locale, code),
    {
      revalidate: false,
    }
  );

export interface UseVariantSelectionInput {
  /** `hit.parentSku` on the PLP; required unless `product` is supplied. */
  productCode?: string;
  /** Lazy trigger — wire to popover open so nothing is fetched until needed. */
  enabled?: boolean;
  /**
   * Pre-hydrated product (e.g. PDP, where the full `MecProduct` is a
   * superset of `QuickAddProduct`) — skips the fetch entirely.
   */
  product?: QuickAddProduct | null;
  initialColour?: {
    colourId?: number;
    colourName?: string;
    colourSku?: string;
  };
}

export type VariantSelectionStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseVariantSelectionResult {
  status: VariantSelectionStatus;
  error: Error | null;
  product: QuickAddProduct | null;
  colours: ColourOption[];
  sizes: SizeOption[];
  hasSizes: boolean;
  selectedColourId: number | null;
  selectedSizeId: number | null;
  selectColour: (colourId: number) => void;
  selectSize: (sizeId: number) => void;
  selectedVariant: MecVariant | undefined;
  selectedAvailability: boolean;
  canAddToCart: boolean;
  /** SWR mutate — refresh stock after an out-of-stock add failure. */
  refresh: () => void;
  /**
   * Warm the SWR cache for this product without opening the popover — wire
   * to a desktop hover/focus intent so the size grid is already resolved by
   * the time the popover opens. No-op once fetched, while open, or when a
   * `product` was supplied directly.
   */
  prefetch: () => void;
}

export function useVariantSelection(
  input: UseVariantSelectionInput
): UseVariantSelectionResult {
  const {
    productCode,
    enabled = false,
    product: providedProduct = null,
    initialColour,
  } = input;

  const locale = useLocale();
  if (!locale) {
    throw new HookError('Locale must be specified to resolve variants');
  }

  const shouldFetch = enabled && !!productCode && !providedProduct;

  const {
    data,
    error: swrError,
    mutate,
  } = useSWR(
    shouldFetch ? quickAddProductCacheKey(locale, productCode) : null,
    (_key: string, loc: string, code: string) =>
      quickAddProductFetcher(loc, code),
    { revalidateOnFocus: false, dedupingInterval: 60_000 }
  );

  // Cache-priming for `prefetch` below: the same key/fetcher the `useSWR`
  // above subscribes to once opened, populated ahead of time via the global
  // mutate so the popover opens straight onto the size grid.
  const { mutate: globalMutate } = useSWRConfig();
  const prefetchedRef = useRef(false);
  const prefetch = useCallback(() => {
    if (
      prefetchedRef.current ||
      providedProduct ||
      !productCode ||
      shouldFetch
    ) {
      return;
    }
    prefetchedRef.current = true;
    void primeQuickAddProductCache(globalMutate, locale, productCode);
  }, [globalMutate, providedProduct, productCode, shouldFetch, locale]);

  // Revalidate on reopen after a failed fetch. `quickAddProductFetcher` never
  // throws (see `useProduct.ts`) — it resolves `null` on any failure — so
  // that `null` is what `dedupingInterval` below caches per product code.
  // Without this, a shopper who hits a transient failure, closes the
  // popover, and reopens it would keep seeing the same failure for up to
  // 60s even after the backend recovers, since nothing else revalidates a
  // fetch failure (`refresh()` elsewhere only runs after a failed *add*).
  const prevShouldFetchRef = useRef(shouldFetch);
  useEffect(() => {
    if (shouldFetch && !prevShouldFetchRef.current && data === null) {
      void mutate();
    }
    prevShouldFetchRef.current = shouldFetch;
  }, [shouldFetch, data, mutate]);

  const product = providedProduct ?? data ?? null;

  // Explicit user picks; `null` = "no pick yet" (colour then falls back to the
  // resolved initial colour below).
  const [userColourId, setUserColourId] = useState<number | null>(null);
  const [selectedSizeId, setSelectedSizeId] = useState<number | null>(null);

  // Reset user picks only when a *different* product resolves — keyed on
  // identity, not object reference, so an SWR revalidation (`refresh()` after
  // an out-of-stock add) does not wipe the selection. Render-phase reset (the
  // React "adjust state during render" pattern) so consumers never observe a
  // stale selection for the new product.
  const productKey = product ? product.entityId ?? product.code ?? null : null;
  const prevProductKeyRef = useRef(productKey);
  if (prevProductKeyRef.current !== productKey) {
    prevProductKeyRef.current = productKey;
    setUserColourId(null);
    setSelectedSizeId(null);
  }

  // Resolved synchronously (not in an effect) so the colour is available on
  // the very first render with data — auto-add consumers depend on that.
  // `initialColour` is excluded from deps so a fresh hint object per render
  // does not re-resolve.
  const initialColourId = useMemo(
    () => (product ? resolveInitialColourId(product, initialColour) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productKey]
  );

  const selectedColourId = userColourId ?? initialColourId;

  const selectColour = useCallback(
    (colourId: number) => {
      setUserColourId(colourId);
      // Keep the current size only if the new colour has an in-stock variant
      // for it — mirrors PdpContext.setSelectedColourId.
      setSelectedSizeId((prevSizeId) => {
        if (prevSizeId == null || !product) return prevSizeId;
        const inStockForNewColour = (product.variants ?? []).some(
          (variant) =>
            variant.colourId === colourId &&
            variant.sizeId === prevSizeId &&
            variant.hasStockAvailable
        );
        return inStockForNewColour ? prevSizeId : null;
      });
    },
    [product]
  );

  const selectSize = useCallback((sizeId: number) => {
    setSelectedSizeId(sizeId);
  }, []);

  const colours = useMemo(
    () => (product ? getColourOptions(product) : []),
    [product]
  );

  const sizes = useMemo(
    () => (product ? getSizeOptionsForColour(product, selectedColourId) : []),
    [product, selectedColourId]
  );

  const hasSizes = !!product?.sizes?.length;

  const selectedVariant = useMemo(
    () =>
      product
        ? resolveVariant(product, selectedColourId, selectedSizeId)
        : undefined,
    [product, selectedColourId, selectedSizeId]
  );

  const selectedAvailability = useMemo(
    () =>
      product
        ? getSelectedAvailability(product, selectedVariant, selectedColourId)
        : false,
    [product, selectedVariant, selectedColourId]
  );

  const canAddToCart =
    !!selectedVariant?.entityId &&
    !!selectedVariant?.hasStockAvailable &&
    selectedAvailability &&
    product?.availabilityStatus !== AvailabilityStatus.unavailable;

  const status = useMemo<VariantSelectionStatus>(() => {
    if (providedProduct) return 'ready';
    if (!shouldFetch) return 'idle';
    if (swrError) return 'error';
    // `productFetcher` never throws; it resolves `null` on 204/failure.
    if (data === null) return 'error';
    if (data === undefined) return 'loading';
    return 'ready';
  }, [providedProduct, shouldFetch, swrError, data]);

  const error = useMemo<Error | null>(() => {
    if (swrError) {
      return swrError instanceof Error ? swrError : new Error(String(swrError));
    }
    if (!providedProduct && shouldFetch && data === null) {
      return new Error(`Product not found for code: ${productCode}`);
    }
    return null;
  }, [swrError, providedProduct, shouldFetch, data, productCode]);

  const refresh = useCallback(() => {
    void mutate();
  }, [mutate]);

  return {
    status,
    error,
    product,
    colours,
    sizes,
    hasSizes,
    selectedColourId,
    selectedSizeId,
    selectColour,
    selectSize,
    selectedVariant,
    selectedAvailability,
    canAddToCart,
    refresh,
    prefetch,
  };
}

export default useVariantSelection;
